/**
 * Build graph data structure for D3 visualization
 */
export function buildGraph(center, relations, dirSim, adj, brandLogos = {}) {
  const nodes = [];
  const links = [];
  const nodeMap = {};
  const linkSet = new Set();

  function addNode(id, level) {
    if (nodeMap[id] !== undefined) {
      if (level < nodes[nodeMap[id]].level) {
        nodes[nodeMap[id]].level = level;
      }
      return;
    }
    nodeMap[id] = nodes.length;
    nodes.push({ id, level, logo: brandLogos[id] });
  }

  function addLink(source, target, level) {
    const key = [source, target].sort().join('||');
    if (linkSet.has(key)) return;
    linkSet.add(key);
    links.push({ source, target, level });
  }

  addNode(center, 0);

  // Level 1
  const level1Set = new Set();
  relations.forEach(([s, t, lv]) => {
    if (lv === 1 && s === center) {
      addNode(t, 1);
      addLink(center, t, 1);
      level1Set.add(t);
    }
  });

  // If no level-1 data (clicking a leaf node), use adjacency
  if (level1Set.size === 0 && adj[center]) {
    [...adj[center]].slice(0, 10).forEach((t) => {
      addNode(t, 1);
      addLink(center, t, 1);
      level1Set.add(t);
    });
  }

  // Level 2 - each level-1 brand's top 10
  level1Set.forEach((brand) => {
    relations.forEach(([s, t, lv]) => {
      if (lv === 2 && s === brand) {
        addNode(t, 2);
        addLink(brand, t, 2);
      }
    });
    // Fallback: use level-1 data or adjacency
    if (
      !relations.some(([s, , lv]) => lv === 2 && s === brand) &&
      dirSim[brand]
    ) {
      [...dirSim[brand]].slice(0, 10).forEach((t) => {
        addNode(t, 2);
        addLink(brand, t, 2);
      });
    }
  });

  // Cross-links between all existing nodes
  nodes.forEach((n) => {
    if (adj[n.id]) {
      adj[n.id].forEach((o) => {
        if (nodeMap[o] !== undefined && o !== n.id) {
          addLink(n.id, o, Math.max(n.level, nodes[nodeMap[o]].level));
        }
      });
    }
  });

  return { nodes, links };
}

/**
 * Get node radius based on level
 */
export function getNodeRadius(d, isMobile) {
  const scale = isMobile ? 0.8 : 1;
  if (d.level === 0) return 44 * scale;
  if (d.level === 1) return 30 * scale;
  return 18 * scale;
}

/**
 * Get brand initials from name
 */
export function getBrandInitials(name) {
  const parts = name.split(/[\s.]+/).filter(Boolean);
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  return parts
    .map((w) => w[0])
    .join('')
    .substring(0, 3)
    .toUpperCase();
}

/**
 * Check if running inside React Native WebView
 */
export function isReactNativeWebView() {
  return typeof window !== 'undefined' && window.ReactNativeWebView;
}

/**
 * Navigate to brand page - handles both WebView and normal browser
 * @param {string} brandName - The brand name to navigate to
 */
export function navigateToBrandPage(brandName) {
  const encodedName = encodeURIComponent(brandName);

  if (isReactNativeWebView()) {
    // Send message to React Native app
    window.ReactNativeWebView.postMessage(
      JSON.stringify({
        type: 'NAVIGATE_TO_BRAND',
        payload: {
          brandName,
          url: `/brand/${encodedName}`,
        },
      })
    );
  } else {
    // Normal browser - open on the current host in a new tab
    const origin = window.location.origin;
    window.open(
      `${origin}/brand/${encodedName}`,
      '_blank'
    );
  }
}
